import { Injectable, Inject, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { STORAGE_KEYS } from '../constants/api.constants';
import { CalendarUpdateMessage } from '../interfaces/calendar-event.interface';

@Injectable({
  providedIn: 'root'
})
export class CalendarWebSocketService {
  private socket: WebSocket | null = null;
  private calendarUpdate$ = new Subject<CalendarUpdateMessage>();
  private reconnectAttempts = 0;
  private maxReconnectDelay = 30000;
  private reconnectTimer: any = null;
  private isManuallyClosed = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone,
  ) {}

  connect(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return;

    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return;

    this.isManuallyClosed = false;
    const wsUrl = `${environment.wsUrl}/calendar/ws?token=${encodeURIComponent(token)}`;

    this.ngZone.runOutsideAngular(() => {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        this.reconnectAttempts = 0;
        console.log('Naptár WebSocket csatlakozva');
      };

      this.socket.onmessage = (event: MessageEvent) => {
        try {
          const message: CalendarUpdateMessage = JSON.parse(event.data);
          if (message.type === 'ping') {
            this.socket?.send('ping');
            return;
          }
          if (message.type === 'calendar_update') {
            this.ngZone.run(() => {
              this.calendarUpdate$.next(message);
            });
          }
        } catch (e) {
          // Nem JSON üzenet (pl. pong) — figyelmen kívül hagyjuk
        }
      };

      this.socket.onclose = (event: CloseEvent) => {
        console.log(`Naptár WebSocket lezárva: code=${event.code}`);
        this.socket = null;
        if (!this.isManuallyClosed) {
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = () => {
        // Az onclose kezelni fogja az újracsatlakozást
      };
    });
  }

  disconnect(): void {
    this.isManuallyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  onCalendarUpdate(): Observable<CalendarUpdateMessage> {
    return this.calendarUpdate$.asObservable();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    const delay = Math.min(
      Math.pow(2, this.reconnectAttempts) * 1000,
      this.maxReconnectDelay
    );
    this.reconnectAttempts++;

    console.log(`Naptár WebSocket újracsatlakozás ${delay / 1000}s múlva...`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
